import { IconButton, CircularProgress, Menu, MenuItem, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { differenceInDays } from "date-fns";
import { formatDisplayDate } from "utils/dateFormat";
import { ALL_COLUMNS_VISIBLE, withFilteredStatusesVisible } from "./kanbanColumnVisibility";
import { applyPendingMoves } from "./kanbanOptimisticMove";
import {
  DndContext,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { toast } from "react-toastify";
import { useDeleteTask } from "utils/taskService";
import {
  getSubtaskProgress,
  getTaskAssignees,
  getTaskPriorityDotColor,
  getTaskStatusDotColor,
} from "utils/taskUtils";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import TaskAssigneeAvatars from "components/Tasks/TaskAssigneeAvatars";
import PriorityBadge from "components/Tasks/PriorityBadge";

const COLUMN_TITLES = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  HOLD: "On Hold",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

/** Closed work has no deadline pressure; everything else reports days left and whether it slipped. */
const getDeadlineState = (task) => {
  const daysUntilDeadline = differenceInDays(new Date(task?.deadline), new Date());
  if (task?.status === "COMPLETED" || task?.status === "CANCELLED") return false;
  return { days: daysUntilDeadline + 1, isPassed: daysUntilDeadline < 0 };
};

/** Deleting a task takes the right the server reports as canEditDetails: the assigner
 *  or an org super admin. TableView and ViewTask read the same flag. */
const canCurrentUserDeleteTask = (task) => Boolean(task?.canEditDetails);

const KanbanCard = memo(({ task, isUpdating, onOpen, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });

  const [menuAnchor, setMenuAnchor] = useState(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const deadlineInfo = getDeadlineState(task);
  const assignees = getTaskAssignees(task);
  const subtaskProgress = getSubtaskProgress(task);
  const canDelete = canCurrentUserDeleteTask(task);

  // Handle title click - prevent drag
  // The whole card is the target — clicking the title only was easy to miss. The drag handle
  // and the menu stop propagation so they keep doing their own thing.
  const handleCardClick = () => onOpen(task);

  // Handle menu open
  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setMenuAnchor(null);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    if (!canDelete) return;
    onDelete(task);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={handleCardClick}
      title="Click to view task"
      className={`relative bg-app-surface rounded-xl shadow-sm border border-app-border p-4 mb-3 cursor-pointer hover:shadow-md hover:border-primary transition-all duration-200 ${
        isDragging ? "shadow-lg scale-105" : ""
      }`}
    >
      {isUpdating && (
        <div className="absolute inset-0 bg-app-surface/90 backdrop-blur-sm flex items-center justify-center rounded-xl z-10">
          <CircularProgress size={24} />
        </div>
      )}
      {/* Priority Indicator */}
      <div
        className="absolute top-0 left-0 w-1 h-full rounded-l-xl"
        style={{ backgroundColor: getTaskPriorityDotColor(task?.priority) }}
      />
      {/* Header with Title and Actions */}
      <div className="flex items-start justify-between mb-1.5">
        {/* Drag Handle */}
        <div
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          title="Drag to change status"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-app-surface-hover rounded mr-1.5 flex-shrink-0 mt-0.5"
          style={{ touchAction: "none" }}
        >
          <svg className="w-4 h-4 text-app-text-muted" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 5h2v2H9V5zm0 6h2v2H9v-2zm0 6h2v2H9v-2zm4-12h2v2h-2V5zm0 6h2v2h-2v-2zm0 6h2v2h-2v-2z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0 pr-2">
          <h4 className="font-semibold text-sm text-app-text truncate select-none leading-snug">
            {task?.specificTask || task?.comment || "Untitled task"}
          </h4>
          <p className="text-xs text-app-text-muted truncate mt-0.5">
            {task?.clientName || "No client"}
          </p>
        </div>
        {/* Three Dot Menu — Delete is its only item, and deleting takes the same right the server
            reports as canEditDetails (the assigner or an org super admin), so the menu goes with it. */}
        {canDelete && (
          <>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              className="hover:bg-app-surface-hover flex-shrink-0"
              sx={{ padding: "4px" }}
              aria-label="Task actions"
            >
              <MoreVertIcon className="text-app-text-secondary text-base" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
                Delete
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
      {/* Priority and Module Badges */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <PriorityBadge priority={task?.priority} size="sm" />
        <span className="text-[11px] text-app-text-muted bg-app-surface-muted px-2 py-1 rounded-md font-medium">
          {task?.module || "N/A"}
        </span>
      </div>
      {/* Assigned To */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-app-border">
        <span className="text-xs text-app-text-secondary">
          <span className="font-medium text-app-text">Assigned:</span> {assignees[0]?.name || "N/A"}
          {assignees.length > 1 && ` +${assignees.length - 1}`}
        </span>
        <TaskAssigneeAvatars people={assignees} max={3} size={20} />
      </div>
      {subtaskProgress.total > 0 && (
        <div className="flex items-center gap-1.5 mb-3 text-xs text-app-text-secondary">
          <span className="font-medium text-app-text">Subtasks:</span>
          {subtaskProgress.done}/{subtaskProgress.total}
        </div>
      )}
      {/* Deadline Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <svg
            className="w-3.5 h-3.5 text-app-text-muted"
            style={deadlineInfo?.isPassed ? { color: "var(--app-priority-high-text)" } : undefined}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span
            className="text-xs font-medium text-app-text-secondary"
            style={deadlineInfo?.isPassed ? { color: "var(--app-priority-high-text)" } : undefined}
          >
            {formatDisplayDate(task?.deadline)}
          </span>
        </div>
        {deadlineInfo?.days > 0 && (
          <span className="text-xs text-app-text-secondary bg-app-surface-muted px-2 py-0.5 rounded">
            {deadlineInfo.days} day{deadlineInfo.days > 1 ? "s" : ""} left
          </span>
        )}
        {deadlineInfo?.days <= 0 && deadlineInfo !== false && (
          <span className="text-xs font-semibold bg-app-priority-high-bg text-app-priority-high-text px-2 py-0.5 rounded">
            Overdue
          </span>
        )}
      </div>
    </div>
  );
});
KanbanCard.displayName = "KanbanCard";

const KanbanColumn = memo(
  ({
    column,
    isOver,
    isFetching,
    onHide,
    onStatusPageChange,
    updatingTaskId,
    onOpenTask,
    onDeleteTask,
  }) => {
    // Still registered so an empty column is a target; the highlight itself comes from the
    // resolved column, because isOver stays false while the cursor is over one of the cards.
    const { setNodeRef } = useDroppable({ id: column.id });

    const handleHideColumn = (e) => {
      e.stopPropagation();
      onHide(column.id);
    };

    return (
      <div
        ref={setNodeRef}
        className={`flex-1 min-w-[260px] bg-app-surface-muted rounded-xl p-4 mx-2 transition-all duration-200 ${
          isOver
            ? "bg-app-surface-hover border-2 border-primary shadow-lg"
            : "border border-app-border"
        }`}
      >
        {/* Column Header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-app-border">
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: getTaskStatusDotColor(column.id) }}
            />
            <h3 className="font-bold text-base text-app-text">{column.title}</h3>
            <Tooltip
              title={
                column.totalPages > 1
                  ? `${column.totalItems} ${column.title.toLowerCase()} tasks in total — ${
                      column.tasks.length
                    } shown on this page`
                  : `${column.totalItems} ${column.title.toLowerCase()} tasks`
              }
            >
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-app-surface text-app-text border border-app-border min-w-[22px] text-center">
                {column.totalItems}
                {column.totalPages > 1 ? " total" : ""}
              </span>
            </Tooltip>
          </div>
          <IconButton
            onClick={handleHideColumn}
            size="small"
            className="hover:bg-app-surface-hover"
            title="Hide column"
            sx={{ padding: "4px" }}
          >
            <CloseIcon className="text-app-text-secondary text-base" />
          </IconButton>
        </div>

        {/* Tasks Container */}
        <SortableContext
          items={column.tasks?.map((task) => task.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3 min-h-[400px] max-h-[calc(100vh-300px)] overflow-y-auto pr-1">
            {column.tasks?.map((task) => (
              <KanbanCard
                key={task.id}
                task={task}
                isUpdating={updatingTaskId === task.id}
                onOpen={onOpenTask}
                onDelete={onDeleteTask}
              />
            ))}
            {column.tasks?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-app-text-muted">
                <svg
                  className="w-12 h-12 mb-2 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <p className="text-sm font-medium">No tasks</p>
              </div>
            )}
          </div>
        </SortableContext>

        {column.totalItems > 0 && (
          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-app-border">
            {/* Spelled out rather than "5 of 22": a bare count next to a column of five cards
                reads as "this column has five tasks", which is what people got wrong. */}
            <span className="text-sm font-medium text-app-text-secondary">
              Showing{" "}
              <span className="text-app-text font-bold">
                {column.firstShown}–{column.lastShown}
              </span>{" "}
              of <span className="text-app-text font-bold">{column.totalItems}</span>
            </span>
            {column.totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Tooltip title="Previous page">
                  <span>
                    <IconButton
                      disabled={isFetching || column.currentPage <= 0}
                      onClick={() => onStatusPageChange?.(column.id, column.currentPage - 1)}
                      className="border border-app-border"
                      sx={{ padding: "6px", borderRadius: "8px" }}
                    >
                      <ChevronLeftIcon fontSize="medium" />
                    </IconButton>
                  </span>
                </Tooltip>
                <span className="text-sm font-bold text-app-text tabular-nums px-1.5">
                  {column.currentPage + 1}/{column.totalPages}
                </span>
                <Tooltip title="Next page">
                  <span>
                    <IconButton
                      disabled={isFetching || column.currentPage >= column.totalPages - 1}
                      onClick={() => onStatusPageChange?.(column.id, column.currentPage + 1)}
                      className="border border-app-border"
                      sx={{ padding: "6px", borderRadius: "8px" }}
                    >
                      <ChevronRightIcon fontSize="medium" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
KanbanColumn.displayName = "KanbanColumn";

const KanbanView = ({
  taskGroups,
  statuses,
  onStatusPageChange,
  onShowClosedTasks,
  statusFilter,
  token,
  refetch,
  isFetching,
  API_URL,
}) => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);
  const [overColumnId, setOverColumnId] = useState(null);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  // Drops that have been sent but not yet confirmed by a refetch, as { taskId: newStatus }.
  const [pendingMoves, setPendingMoves] = useState({});
  const [taskPendingDeletion, setTaskPendingDeletion] = useState(null);

  // Every column starts on; the header X hides one for this session only.
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS_VISIBLE);

  // Filtering to a status the user had hidden must not render an empty board.
  useEffect(() => {
    setVisibleColumns((prev) => withFilteredStatusesVisible(prev, statusFilter));
  }, [statusFilter]);

  const boardGroups = useMemo(
    () => applyPendingMoves(taskGroups, pendingMoves),
    [taskGroups, pendingMoves]
  );

  // A confirmed refetch already carries the move, so the optimistic copy is dropped as soon
  // as fresh data arrives -- keeping it would double-count on the next render.
  useEffect(() => {
    setPendingMoves({});
  }, [taskGroups]);

  // Each status is paginated on its own by the backend, so a column's cards are its group's
  // page rather than a client-side filter over one shared page of tasks.
  const allKanbanColumnsConfig = useMemo(
    () =>
      (statuses ?? []).map((status) => {
        const group = boardGroups?.[status];
        const tasks = group?.content ?? [];
        const currentPage = Number(group?.currentPage) || 0;
        const totalItems = Number(group?.totalItems) || 0;
        const totalPages = Number(group?.totalPages) || 0;
        // Derived from the page rather than the filter's page size: the last page is short,
        // and pageSize * page would overstate where this page starts.
        const pageSize = totalPages > 1 ? Math.ceil(totalItems / totalPages) : tasks.length;
        const firstShown = tasks.length === 0 ? 0 : currentPage * pageSize + 1;
        return {
          id: status,
          title: COLUMN_TITLES[status] ?? status,
          tasks,
          totalItems,
          totalPages,
          currentPage,
          firstShown,
          lastShown: tasks.length === 0 ? 0 : firstShown + tasks.length - 1,
        };
      }),
    [statuses, boardGroups]
  );

  const allTasks = useMemo(
    () => allKanbanColumnsConfig.flatMap((column) => column.tasks),
    [allKanbanColumnsConfig]
  );

  // Filtered kanban columns based on visibility
  const kanbanColumns = useMemo(() => {
    return allKanbanColumnsConfig.filter((column) => visibleColumns[column.id]);
  }, [allKanbanColumnsConfig, visibleColumns]);

  // Column visibility handler. Showing a closed-work column also has to widen the status
  // filter, otherwise the column would come back permanently empty.
  const handleColumnToggle = useCallback(
    (columnId) => {
      setVisibleColumns((prev) => {
        const nowVisible = !prev[columnId];
        if (nowVisible && (columnId === "COMPLETED" || columnId === "CANCELLED")) {
          onShowClosedTasks?.();
        }
        return { ...prev, [columnId]: nowVisible };
      });
    },
    [onShowClosedTasks]
  );

  // DnD Kit sensors
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // One mutation for the whole board. It used to be created per card, so every render built
  // a fresh mutation for each of them.
  const { mutate: deleteTask } = useDeleteTask({
    onSuccess: () => {
      setTaskPendingDeletion(null);
      setUpdatingTaskId(null);
      refetch();
      toast.success("Task deleted successfully");
    },
    onError: (error) => {
      setUpdatingTaskId(null);
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not delete the task"
      );
    },
  });

  // Stable identities, so memoised columns and cards re-render only when their own data moves.
  const handleOpenTask = useCallback(
    (task) => navigate(`/admin/tasks/view/${task.id}`, { state: { item: task } }),
    [navigate]
  );

  // Asking through the shared dialog rather than window.confirm, like every other
  // destructive action in the app. Only users the server grants canEditDetails may delete.
  const handleDeleteTask = useCallback((task) => {
    if (!canCurrentUserDeleteTask(task)) {
      toast.error("You do not have permission to delete this task");
      return;
    }
    setTaskPendingDeletion(task);
  }, []);

  const confirmDeleteTask = useCallback(() => {
    if (!taskPendingDeletion) return;
    if (!canCurrentUserDeleteTask(taskPendingDeletion)) {
      toast.error("You do not have permission to delete this task");
      setTaskPendingDeletion(null);
      return;
    }
    setUpdatingTaskId(taskPendingDeletion.id);
    deleteTask(taskPendingDeletion.id);
  }, [deleteTask, taskPendingDeletion]);

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  /**
   * A drop lands on whichever droppable is under the cursor, and in a column with cards in it
   * that is a card, not the column. Map a card back to the column holding it so a full column
   * is as good a target as an empty one.
   */
  const resolveColumnId = useCallback(
    (overId) => {
      if (overId == null) return null;
      if (allKanbanColumnsConfig.some((column) => column.id === overId)) return overId;
      return (
        allKanbanColumnsConfig.find((column) => column.tasks?.some((task) => task.id === overId))
          ?.id ?? null
      );
    },
    [allKanbanColumnsConfig]
  );

  /**
   * pointerWithin only returns droppables the cursor is literally inside, so a dense column
   * no longer loses to its own cards the way closestCenter made it. rectIntersection covers
   * the gaps between columns, where the pointer is inside nothing.
   */
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
  }, []);

  const handleDragOver = (event) => {
    setOverColumnId(resolveColumnId(event.over?.id));
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverColumnId(null);
  };

  // Handle drag end - update task status
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);

    if (!over) return;

    const taskId = active.id;
    // Dropping onto a card counts as dropping into that card's column; this used to bail out.
    const newStatus = resolveColumnId(over.id);
    if (!newStatus) return;

    // Find the task
    const task = allTasks.find((t) => t.id === taskId);
    if (!task) return;

    // Normalize status for comparison
    const currentStatus = task.status?.toUpperCase().replace(/\s+/g, "_");
    const normalizedNewStatus = newStatus.toUpperCase();

    // Don't update if status hasn't changed
    if (currentStatus === normalizedNewStatus) return;

    // Show the card in its new column now; the request confirms it or we put it back.
    setPendingMoves((prev) => ({ ...prev, [taskId]: normalizedNewStatus }));

    try {
      // Status only. Resending the whole task here used to clear every field the payload
      // left out -- the price list, the checklist and the entire collaborator list.
      await axios.patch(
        `${API_URL}/tasks/${taskId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Task status updated successfully");
      refetch();
    } catch (error) {
      setPendingMoves((prev) => {
        const { [taskId]: rolledBack, ...rest } = prev;
        return rest;
      });
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Error updating task status"
      );
    }
  };

  // Kanban Card Component

  // Kanban Column Component

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 pt-4 px-2">
        {kanbanColumns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            isOver={overColumnId === column.id && activeId != null}
            isFetching={isFetching}
            onHide={handleColumnToggle}
            onStatusPageChange={onStatusPageChange}
            updatingTaskId={updatingTaskId}
            onOpenTask={handleOpenTask}
            onDeleteTask={handleDeleteTask}
          />
        ))}
        {kanbanColumns.length === 0 && (
          <div className="flex items-center justify-center w-full py-12">
            <p className="text-app-text-secondary text-sm">
              All columns are hidden. Please refresh the page to show all columns.
            </p>
          </div>
        )}
      </div>
      <DragOverlay>
        {activeId ? (
          <div className="bg-app-surface rounded-xl shadow-2xl p-4 border-2 border-primary opacity-95 transform rotate-2">
            <div className="font-bold text-sm text-app-text">
              {allTasks.find((t) => t.id === activeId)?.specificTask ||
                allTasks.find((t) => t.id === activeId)?.clientName ||
                "Task"}
            </div>
            <div className="text-xs text-app-text-secondary mt-1">
              {allTasks.find((t) => t.id === activeId)?.module || ""}
            </div>
          </div>
        ) : null}
      </DragOverlay>
      <ConfirmDialog
        open={taskPendingDeletion != null}
        title="Delete task?"
        message={`"${
          taskPendingDeletion?.specificTask || taskPendingDeletion?.clientName || "This task"
        }" will be deleted along with its subtasks, comments and activity. This cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={updatingTaskId === taskPendingDeletion?.id}
        onConfirm={confirmDeleteTask}
        onCancel={() => setTaskPendingDeletion(null)}
      />
    </DndContext>
  );
};

export default KanbanView;
